import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, ElementRef, forwardRef, Input, NgZone, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as jsonconverter from 'json-to-json-schema';
import { isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { UtilService } from 'src/app/services/util.service';
import { XmlToXsdConverterService } from 'src/app/services/xml-to-xsd-converter.service';
import * as vkbeautify from 'vkbeautify';
import { validateXml, xml2xsd } from 'xsdlibrary';

@Component({
  selector: 'vb-json-editor',
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonEditorComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class JsonEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private ms = inject(MessageService);
  private zone = inject(NgZone);
  private utilService = inject(UtilService);
  private xmlToXsdService = inject(XmlToXsdConverterService);

  @Input() element: any;
  @Input() config: any;
  @Input() disableField = false;
  private modelValue: any;
  @Input() formData: any = null;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  value: any;
  backup: any;
  schemaBackup: any;
  jsonString: any;
  fromSave = false;
  showEditor = false;
  disabled = false;
  currenttablename: '';
  editorOptions = {
    theme: 'vs-light',
    language: 'json',
    readOnly: this.disableField,
    wordWrap: 'on',
    scrollBeyondLastLine: false,
    padding: { top: 10, bottom: 10 },
    lineNumbersMinChars: 3
  };
  viewerOptions = { theme: 'vs-light', language: 'json', readOnly: true };
  rowCount = 1;
  jsonSchema: any;
  schema = false;
  object = '{ }';
  formFieldDisable = false;
  editJson = false;
  ngOnInit(): void {
    this.editorOptions.language = this.config.language;
    this.schema = this.config?.schema;
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.setValue(value);
    this.modelValue = value;
    if (value) {
      this.editJson = true;
    } else {
      this.editJson = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
    this.formFieldDisable = isDisabled;
  }

  toggleEditor(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      this.backup = this.value;
      this.schemaBackup = this.jsonSchema;
    }
    this.showEditor = !this.showEditor;
    this.currenttablename = this.formData.name;
  }

  onShow(event) {
    window.dispatchEvent(new Event('resize'));
  }

  onResizeEnd(event) {
    window.dispatchEvent(new Event('resize'));
  }

  setValue(value) {
    console.log('setValue');
    console.log(value);
    if (this.schema && value) {
      if (this.editorOptions.language === 'json') {
        this.value = value[0] ? JSON.stringify(value[0], null, '\t') : '';
        this.jsonSchema = value[1] ? JSON.stringify(value[1], null, '\t') : '';
      } else {
        this.value = value[0];
        this.jsonSchema = value[1];
      }
      this.rowCount = 8;
      this.backup = this.value;
    } else if (value) {
      this.jsonString = this.editorOptions.language === 'json' ? JSON.stringify(JSON.parse(value), null, '\t') : value;
      this.value =
        this.editorOptions.language === 'json' ? JSON.stringify(this.getValObject(value), null, '\t') : value;
      this.backup = this.value;
      this.rowCount = 8;
    } else {
      this.value = '';
    }

    if (this.element.lookupTo === 'Workflow') {
      this.editorOptions = {
        theme: 'vs-light',
        language: this.config.language,
        readOnly: true,
        wordWrap: 'on',
        scrollBeyondLastLine: false,
        padding: { top: 10, bottom: 10 },
        lineNumbersMinChars: 3
      };
    } else {
      this.editorOptions = {
        theme: 'vs-light',
        language: this.config.language,
        readOnly: this.formData?.sysgen,
        wordWrap: 'on',
        scrollBeyondLastLine: false,
        padding: { top: 10, bottom: 10 },
        lineNumbersMinChars: 3
      };
    }
  }

  getValObject(value) {
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
    return value;
  }

  setValueToForm() {
    let obj;
    if (this.schema) {
      if (this.value && this.jsonSchema) {
        obj =
          this.editorOptions.language === 'json'
            ? [JSON.parse(this.value), JSON.parse(this.jsonSchema)]
            : [this.value, this.jsonSchema];
      } else if (this.value) {
        obj = this.editorOptions.language === 'json' ? [JSON.parse(this.value), null] : [this.value, null];
      } else if (this.jsonSchema) {
        obj = this.editorOptions.language === 'json' ? [null, JSON.parse(this.jsonSchema)] : [null, this.jsonSchema];
      }
    } else {
      obj = this.editorOptions.language === 'json' ? JSON.parse(this.value) : this.value;
    }
    this.modelValue = obj;
    this.onChange(this.modelValue);
  }

  formatJson() {
    let obj;
    if (this.editorOptions.language === 'json') {
      try {
        obj = JSON.parse(this.value);
        // console.log(obj);
        this.value = JSON.stringify(obj, null, '\t');
      } catch (e) {
        this.showMessage('error', 'Error in entered json. Please verify', e);
      }
    } else {
      this.value = vkbeautify.xml(this.value);
    }
  }

  saveJson() {
    let errFlag = false;
    if (this.editorOptions.language === 'json') {
      if (this.value) {
        try {
          const obj = JSON.parse(this.value);
          // console.log(obj);
        } catch (e) {
          this.showMessage('error', 'Error in entered json. Please verify', e);
          errFlag = true;
        }
      }
      if (this.schema && this.jsonSchema) {
        try {
          const obj = JSON.parse(this.jsonSchema);
          // console.log(obj);
        } catch (e) {
          this.showMessage('error', 'Error in entered json schema. Please verify', e);
          errFlag = true;
        }
      }
    } else if (this.editorOptions.language === 'xml') {
      if (this.value) {
        try {
          const xmlDoc = this.utilService.validateXML(this.value); //is valid XML
        } catch (e) {
          this.showMessage('error', 'Error in entered XML. Please verify', e);
          errFlag = true;
        }
      }
      if (this.schema && this.jsonSchema) {
        try {
          const xmlDoc = this.utilService.validateXML(this.jsonSchema); //is valid XML
        } catch (e) {
          this.showMessage('error', 'Error in entered XML. Please verify', e);
          errFlag = true;
        }
      }
    }
    if (!this.value && !(this.schema && this.jsonSchema)) {
      this.showMessage('error', 'No Data Entered to save');
      errFlag = true;
    }
    this.fromSave = true;
    if (!errFlag) {
      if (this.value?.length) {
        this.rowCount = 8;
        this.jsonString = this.value;
      }
      this.backup = this.value;
      this.schemaBackup = this.jsonSchema;
      this.setValueToForm();
      this.toggleEditor();
    }
  }

  showMessage(status: string, message: string, detail?: string) {
    this.ms.clear();
    this.ms.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  closeDialog(event) {
    if (!isEqual(this.value, this.backup) && !this.fromSave) {
      this.confirmationDialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to close this editor?',
        accept: () => {
          this.value = this.backup;
          this.jsonSchema = this.schemaBackup;
        },
        reject: () => {
          this.showEditor = true;
        }
      });
    }
    this.showEditor = false;
    this.fromSave = false;
  }
  getSchema() {
    if (this.editorOptions.language === 'json') {
      if (!this.value?.trim()) {
        this.jsonSchema = '';
        return;
      }
      this.jsonSchema = this.generateGenericSchema(JSON.parse(this.value));
      this.jsonSchema = JSON.stringify(this.jsonSchema);
      this.formatJsonSchema();
    } else if (this.xmlToXsdService.validateXml(this.value)) {
      try {
        this.jsonSchema = this.xmlToXsdService.convertXmlToXsd(this.value);
        this.formatJsonSchema();
      } catch (e) {
        this.showMessage('error', 'XSD Generation Failed', e instanceof Error ? e.message : 'Unknown error');
      }
    } else {
      this.showMessage('error', 'Invalid XML', 'Please enter valid XML before generating schema');
    }
  }

  clearJosn() {
    this.value = '';
    this.jsonSchema = '';
  }
  formatJsonSchema() {
    let obj;
    if (this.editorOptions.language === 'json') {
      try {
        obj = JSON.parse(this.jsonSchema);
        this.jsonSchema = JSON.stringify(obj, null, '\t');
      } catch (e) {
        this.showMessage('error', 'Error in entered json. Please verify', e);
        this.showEditor = true;
      }
    } else {
      this.jsonSchema = vkbeautify.xml(this.jsonSchema);
    }
  }
  validateData(type: any) {
    if (this.editorOptions.language === 'json') {
      if (type === 'source') {
        try {
          const valid = JSON.parse(this.value);
          this.showMessage('success', 'Valid JSON');
        } catch (e) {
          this.showMessage('error', 'Error in entered json. Please verify');
          this.showEditor = true;
        }
      } else {
        try {
          const valid = JSON.parse(this.jsonSchema);
          this.showMessage('success', 'Valid JSON Schema');
        } catch (e) {
          this.showMessage('error', 'Error in json Schema. Please verify');
          this.showEditor = true;
        }
      }
    } else if (type === 'source') {
      try {
        const valid = this.xmlToXsdService.validateXml(this.value);
        if (valid) {
          this.showMessage('success', 'Valid XML');
        } else {
          this.showMessage('error', 'Error in entered XML. Please verify');
        }
      } catch (e) {
        this.showMessage('error', e instanceof Error && e.message ? e.message : 'Error in entered XML. Please verify');
      }
    } else {
      try {
        const valid = validateXml(this.jsonSchema);
        if (valid) {
          this.showMessage('success', 'Valid XML Schema');
        } else {
          this.showMessage('error', 'Invalid XML Schema');
        }
      } catch (e) {
        this.showMessage('error', e instanceof Error && e.message ? e.message : 'Invalid XML Schema');
      }
    }
  }
  ngOnDestroy(): void {
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
  }

  generateGenericSchema(data: any): any {
    // Convert JSON data to initial schema
    const schema = jsonconverter.convert(data);

    // If it's an array, apply additional processing to handle array items
    if (schema.type === 'array') {
      this.processArraySchema(schema);
    }

    return schema;
  }

  processArraySchema(schema: any) {
    if (schema.items && schema.items.oneOf) {
      const mergedProperties: any = {};
      const allRequired = new Set<string>();
      let commonRequired: Set<string> | null = null;

      // Merge properties from each object in 'oneOf'
      schema.items.oneOf.forEach((item: any) => {
        if (item.properties) {
          Object.keys(item.properties).forEach((key) => {
            mergedProperties[key] = item.properties[key];
          });

          if (item.required) {
            // Initialize or intersect required fields
            if (commonRequired === null) {
              commonRequired = new Set(item.required);
            } else {
              commonRequired = new Set(item.required.filter((req: string) => commonRequired!.has(req)));
            }
          }
        }
      });

      schema.items = {
        type: 'object',
        properties: mergedProperties,
        required: commonRequired ? Array.from(commonRequired) : []
      };
    }
  }
}


